import { ComponentType, lazy } from 'react';
import { logWarning } from 'shared/helpers/Logger';

export const RELOAD_STORAGE_KEY = 'lazy-import-reloaded';
export const RELOAD_COOLDOWN_MS = 60 * 1000;

function canAutoReload(): boolean {
  try {
    const lastReloadTime = Number(sessionStorage.getItem(RELOAD_STORAGE_KEY));
    return (
      !Number.isFinite(lastReloadTime) ||
      Date.now() - lastReloadTime > RELOAD_COOLDOWN_MS
    );
  } catch {
    return false;
  }
}

export function importWithReload(
  factory: () => Promise<Record<string, unknown>>,
): Promise<Record<string, unknown>> {
  return factory()
    .catch(() => factory())
    .catch((error) => {
      if (canAutoReload()) {
        sessionStorage.setItem(RELOAD_STORAGE_KEY, String(Date.now()));
        window.location.reload();
        return new Promise<Record<string, unknown>>(() => {});
      }

      logWarning('Chunk still failing after reload, possible stale cache', {
        error: error instanceof Error ? error.message : String(error),
      });

      throw error;
    });
}

export function lazyImport<T>(
  factory: () => Promise<Record<string, unknown>>,
  name: string,
) {
  return lazy(() =>
    importWithReload(factory).then((module) => ({
      default: module[name] as ComponentType<T>,
    })),
  );
}
